import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useRoleViewFetch } from './RoleViewContext';
import { guardAdminNavigation } from './UnsavedChanges';
import './NotarioDashboard.css';
import './LawyerWorkspace.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const LawyerFolioDetail = React.lazy(() => import('./LawyerFolioDetail'));
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const formatDate = value => value && !Number.isNaN(new Date(value).getTime())
  ? new Date(value).toLocaleDateString('es-MX', { timeZone: 'America/Monterrey' }) : '—';

function Icon({ type }) {
  if (type === 'dashboard') return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="19" height="19"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="19" height="19"><path d="M3 6a2 2 0 0 1 2-2h5l2 3h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>;
}

class DetailErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <div className="nd-card lawyer-error" role="alert"><h3>No se pudo abrir el editor</h3><p>La bandeja de folios sigue disponible. Actualiza la página o vuelve a intentarlo.</p><button className="lawyer-secondary" onClick={() => this.setState({ failed: false })}>Reintentar</button></div>;
    return this.props.children;
  }
}

/** Keep the first render lightweight: office tools are loaded only for an open case. */
export default function LawyerWorkspace() {
  const apiFetch = useRoleViewFetch();
  const [items, setItems] = useState([]);
  const [view, setView] = useState('dashboard');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');

  const request = useCallback(async (path = '') => {
    const res = await apiFetch(`${API}/api/admin/poderes${path}`, { headers: { 'x-auth-token': token() } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.msg || 'No se pudieron obtener los folios.');
    return data;
  }, [apiFetch]);

  const reload = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await request();
      setItems(Array.isArray(data.poderes) ? data.poderes : []);
    } catch (e) { setError(e.message || 'No se pudieron cargar los folios.'); }
    finally { setLoading(false); }
  }, [request]);

  const open = useCallback(async id => {
    setDetailLoading(true); setDetailError('');
    try {
      const data = await request(`/${id}`);
      if (!data.poder) throw new Error('No se encontró el expediente.');
      let current = data.poder;
      if (!current.serviceStartedAt) {
        const response = await apiFetch(`${API}/api/admin/poderes/${id}/open-service`, {
          method: 'POST',
          headers: { 'x-auth-token': token(), 'Content-Type': 'application/json' },
          body: '{}',
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          // La falta de correo o una validación pendiente no impide consultar el acta.
          setDetailError(result.msg || 'No se pudo registrar automáticamente el inicio del servicio.');
        } else {
          current = { ...current, ...(result.poder || {}) };
          setItems(list => list.map(p => p._id === id ? { ...p, ...current } : p));
        }
      }
      setSelected(current);
      setView('folios');
    } catch (e) { setDetailError(e.message || 'No se pudo abrir el expediente.'); }
    finally { setDetailLoading(false); }
  }, [request, apiFetch]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('order');
    if (id && /^[a-f\d]{24}$/i.test(id)) open(id);
  }, [open]);

  const stats = useMemo(() => {
    const active = items.filter(p => !['finalizado', 'entregado'].includes(p.status) && !['finalizado', 'entregado'].includes(p.notarialWorkflow?.stage));
    const review = active.filter(p => ['pendiente_revision_interna', 'correcciones_solicitadas_admin', 'en_revision_notarial'].includes(p.status));
    const coming = active.filter(p => p.serviceDeadlineAt && new Date(p.serviceDeadlineAt).getTime() >= Date.now() && new Date(p.serviceDeadlineAt).getTime() <= Date.now() + 7 * 86400000);
    return { active: active.length, review: review.length, coming: coming.length };
  }, [items]);

  return <div className="notario-dashboard lawyer-workspace">
    <aside className="nd-sidebar">
      <div className="nd-brand"><div className="nd-brand-mark"><Icon type="folder"/></div><div><div className="nd-brand-title">WISBY</div><div className="nd-brand-sub">Portal de abogados</div></div></div>
      <nav className="nd-nav" aria-label="Portal del abogado Wisby">
        <button type="button" className={`nd-nav-btn ${view === 'dashboard' ? 'active' : ''}`} onClick={() => { setView('dashboard'); setSelected(null); }}><Icon type="dashboard"/> Dashboard</button>
        <button type="button" className={`nd-nav-btn ${view === 'folios' ? 'active' : ''}`} onClick={() => setView('folios')}><Icon type="folder"/> Mis folios asignados</button>
      </nav>
      <div className="nd-user"><div className="nd-user-name">Abogado de Wisby</div><div className="nd-user-email">Acceso a los folios asignados a tu cuenta</div></div>
    </aside>
    <main className="nd-main">
      <header className="nd-topbar"><span className="lawyer-eyebrow">PORTAL DE ABOGADOS · WISBY</span><h1>{view === 'dashboard' ? 'Dashboard de abogado' : 'Mis folios asignados'}</h1><span className="nd-topbar-sub">Tus órdenes de servicio y herramientas de revisión</span></header>
      <section className="nd-content">
        {view === 'dashboard' && <div className="nd-stats-grid">
          <div className="nd-stat-card nd-stat-primary"><div className="nd-stat-value">{items.length}</div><div className="nd-stat-label">Folios asignados</div></div>
          <div className="nd-stat-card nd-stat-info"><div className="nd-stat-value">{stats.review}</div><div className="nd-stat-label">Para revisar</div></div>
          <div className="nd-stat-card nd-stat-warn"><div className="nd-stat-value">{stats.active}</div><div className="nd-stat-label">Activos</div></div>
          <div className="nd-stat-card nd-stat-success"><div className="nd-stat-value">{stats.coming}</div><div className="nd-stat-label">Plazo próximo · 7 días</div></div>
        </div>}
        <div className="nd-card nd-table-card">
          <div className="nd-card-head lawyer-orders-header"><h3>Órdenes de servicio asignadas</h3><button type="button" className="nd-link lawyer-refresh-button" onClick={reload} disabled={loading}><span aria-hidden="true">↻</span> Actualizar</button></div>
          {error && <p className="nd-error" role="alert">{error}</p>}
          {detailError && <p className="nd-error" role="alert">{detailError}</p>}
          {loading ? <p className="nd-muted lawyer-loading" role="status">Cargando tus folios asignados…</p> :
            !items.length ? <div className="nd-empty"><p>No tienes folios asignados todavía.</p><span>Cuando un administrador de Wisby te asigne una orden, aparecerá aquí.</span></div> :
            <div className="nd-table-wrap"><table className="nd-table">
              <thead><tr><th>Folio</th><th>Empresa</th><th>Estado</th><th>Fecha recepción</th><th>Plazo del servicio</th><th>Acción</th></tr></thead>
              <tbody>{items.map(p => <tr key={p._id}>
                <td><strong>{p.folio || '—'}</strong></td><td>{p.companyName || '—'}</td>
                <td>{p.status?.replace(/_/g, ' ') || '—'}</td><td>{formatDate(p.createdAt)}</td><td>{formatDate(p.serviceDeadlineAt)}</td>
                <td><button type="button" className="nd-view-btn" disabled={detailLoading} onClick={() => guardAdminNavigation(() => open(p._id))}>Revisar folio</button></td>
              </tr>)}</tbody>
            </table></div>}
        </div>
        {detailLoading && <div className="nd-card lawyer-loading" role="status">Abriendo expediente…</div>}
        {selected && !detailLoading && <DetailErrorBoundary key={selected._id}>
          <Suspense fallback={<div className="nd-card lawyer-loading" role="status">Abriendo editor del Acta Wisby…</div>}>
            <LawyerFolioDetail poder={selected}
              onChanged={next => setSelected(current => ({ ...current, ...next }))}
              onClose={() => { setSelected(null); reload(); }} />
          </Suspense>
        </DetailErrorBoundary>}
      </section>
    </main>
  </div>;
}
